import { Events } from '@shared/Events';
import './Dialog.scss';

type DialogEvent = 'open' | 'dialog_open' | 'close' | 'init' | 'submit';

export interface IDialogOptions {
    id?: string;
    title?: string; // ... or put data-title='' on $body element
    titleIcon?: string; // 'iki iki-edit'
    width?: number; // max width
    height?: number; // min height
    $body?: JQuery; // dialog html from jsp
    $footer?: JQuery; // optional footer html from jsp
    bodyTemplate?: Function; // handlebars template function to render the body - Handlebars.compile($('#tmpl').html())

    bodyPadding?: number; // padding applied to body in px
    invertStyle?: boolean; // switch body<>header colors
    animated?: boolean; // use open/close animation
    blurBehind?: boolean; // blur content behind dialog
    autoFocus?: boolean; // focus on first input field
    startMaximized?: boolean;

    submitBtnShow?: boolean; // buttons customization
    submitBtnLabel?: string;
    cancelBtnShow?: boolean;
    cancelBtnLabel?: string;
    maximizeBtnShow?: boolean;

    closeOnEsc?: boolean; // close on escape key
    closeOnSubmit?: boolean; // close after successful submit
    destroyOnClose?: boolean; // remove dialog html after it is closed
    restoreInitialState?: boolean; // todo, now it just clears all input elements

    // callbacks for dialog actions, they will be called with (dialog, openData) params
    // openData is any data with which dialog.open(data) is called
    // callbacks can return promise which will put dialog into loading state, or print errors
    onOpen?: (dialog: Dialog, data: any) => any;
    onClose?: (dialog: Dialog, data: any) => any;
    onCancel?: (dialog: Dialog, data: any) => any;
    onSubmit?: (dialog: Dialog, data: any) => any;
    onExpand?: (expanded: boolean) => any;
}

/**
 * INPUT - Dialog shorthand
 * Shows a dialog with single value input
 *
 * ui.input('msg', callback(val), 'initial val')
 */
export function input(title = '', callback?: (val) => any, initialVal = '') {
    return new Dialog({
        title,
        titleIcon: 'iki iki-edit',
        $body: $(`<div><input type="text" name="dialoginput" style="width: 100%; height: auto; padding: 8px; font-size: 16px;"/></div>`),
        width: 200,
        height: 50,
        destroyOnClose: true,

        onOpen: dialog => {
            dialog.$body.find('input').val(initialVal);
        },

        onSubmit: dialog => {
            const input: string = dialog.$('input').val();
            callback.call(dialog, ('' + input).trim());
            dialog.close();
        },
    }).open();
}

/**
 * CONFIRM - Dialog shorthand
 * Shows a confirm dialog
 */
interface IDialogConfirmOptions {
    msg?: string;
    type?: 'success' | 'danger';
    submitBtnLabel?: string;
    width?: number;
}

export function DialogConfirm(opts: IDialogConfirmOptions, onSubmit, onCancel?): Dialog {
    const defaults: IDialogConfirmOptions = {
        msg: kuura.getMessage('general.confirm'),
        submitBtnLabel: kuura.getMessage('general.confirm'),
        type: 'danger',
        width: 350,
    };

    const options = {
        ...defaults,
        ...opts,
    };

    const dialog = new Dialog({
        title: kuura.getMessage('general.confirm'),
        $body: $(`<div class="confirm-text">${options.msg}</div>`),
        width: options.width,
        height: 120,
        cancelBtnLabel: kuura.getMessage('general.cancel'),
        submitBtnLabel: options.submitBtnLabel,
        closeOnSubmit: true,
        onSubmit,
        onCancel,

        onOpen: dialog => {
            dialog.$dialog.addClass('confirm');
            dialog.$btnSubmit.removeClass('ik-button-primary').addClass(`btn-${options.type}`);
        },
    });

    dialog.open();
    return dialog;
}

/**
 * Dialog component
 */
export class Dialog {
    static currentlyOpen: Dialog = null;

    static defaults: IDialogOptions = {
        id: null,
        title: '&nbsp;',
        titleIcon: '',
        width: 550,
        height: 200,
        bodyPadding: 20,
        invertStyle: false,
        animated: true,
        blurBehind: true,
        maximizeBtnShow: false,
        submitBtnShow: true,
        submitBtnLabel: kuura.getMessage('general.confirm'),
        cancelBtnShow: true,
        cancelBtnLabel: kuura.getMessage('general.close'),
        destroyOnClose: false,
        closeOnEsc: true,
        closeOnSubmit: false,
        restoreInitialState: true,
        autoFocus: true,
        startMaximized: false,
    };

    options: IDialogOptions;
    openData: any = {};

    $dialog: JQuery;
    $body: JQuery;
    $footer: JQuery;
    $header: JQuery;
    $bg: JQuery;
    $headerTitle: JQuery;
    $error: JQuery;
    $btnClose: JQuery;
    $btnMaximize: JQuery;
    $btnSubmit: JQuery;
    $btnCancel: JQuery;
    originalBodyOverflow: any;

    constructor(opts: IDialogOptions = {}) {
        if (!(this instanceof Dialog)) {
            return new Dialog(opts);
        }

        this.options = _.defaults(opts, Dialog.defaults);

        // make html
        this.$bg = $('<div class="infrakit-dialog__bg">');
        this.$bg.addRemoveClass(this.options.animated, 'animated');

        this.$dialog = $('<div class="infrakit-dialog">');
        if (this.options.id) {
            this.$dialog[0].id = this.options.id;
        }
        this.$header = $('<div class="infrakit-dialog__header">');
        this.$headerTitle = $('<div class="infrakit-dialog__title">');
        this.$body = $('<div class="infrakit-dialog__body">');
        this.$footer = $('<div class="infrakit-dialog__footer">');
        this.$error = $('<div class="infrakit-dialog__error">');
        this.$btnClose = $('<div class="infrakit-dialog__btn infrakit-dialog__close"><i class="iki iki-cross"></i></div>');
        this.$btnMaximize = $('<div class="infrakit-dialog__btn infrakit-dialog__expand"><i class="iki iki-expand"></i></div>');

        this.$header.append(this.$headerTitle).append(this.$btnClose);
        if (this.options.maximizeBtnShow) {
            this.$header.append(this.$btnMaximize);
        }

        this.$dialog.append(this.$header).append(this.$body);
        this.$dialog.css('max-width', this.options.width);
        this.$dialog.css('min-height', this.options.height);

        // build footer buttons
        if (this.options.$footer) {
            this.options.$footer.show().css('overflow', 'hidden');
            this.$btnSubmit = this.options.$footer.find('.btn-submit');
            this.$btnCancel = this.options.$footer.find('.btn-cancel');
            this.$footer.append(this.options.$footer);
            this.$dialog.append(this.$footer);
        } else {
            this.$btnSubmit = $(`<button class="ik-button-primary dialog-submit-btn">${this.options.submitBtnLabel}</button>`);
            this.$btnCancel = $(`<button class="ik-button-secondary--subtle dialog-cancel-btn">${this.options.cancelBtnLabel}</button>`);

            if (this.options.cancelBtnShow) {
                this.$footer.append(this.$btnCancel);
            }
            if (this.options.submitBtnShow) {
                this.$footer.append(this.$btnSubmit);
            }
            if (this.options.submitBtnShow || this.options.cancelBtnShow) {
                this.$dialog.append(this.$footer);
            } else {
                this.$dialog.addClass('no-footer');
            }
        }

        // invert header<>body colors
        if (this.options.invertStyle) {
            this.$dialog.addClass('invert-style');
        }

        this.$bg.append(this.$dialog);

        // append external body
        if (this.options.$body) {
            this.options.$body.show();
            this.options.$body.css('margin', this.options.bodyPadding);
            this.$body.append(this.options.$body);
        }
        $('body').prepend(this.$bg);

        // set title
        this.options.title = (opts.$body && opts.$body.data('title')) || this.options.title;
        this.setTitle(this.options.title, this.options.titleIcon);
    }

    destroy() {
        this.unbindEvents();
        this.$bg.remove();
    }

    open(data: any = {}): Dialog {
        this.openData = data;

        // close currently open dialog, and open this after current one is closed
        if (Dialog.currentlyOpen) {
            Dialog.currentlyOpen.close();
            setTimeout(() => {
                this.open();
            }, 400);
            return;
        }

        // hide body overflow
        const body = $('html');
        this.originalBodyOverflow = body.css('overflow');
        body.css('overflow', 'hidden');

        // start maximized
        if (this.options.startMaximized) {
            this.toggleMaximize(true);
        }

        // restore initial state
        if (this.options.restoreInitialState) {
            this.restoreInitailState();
        }
        this.$btnSubmit.prop('disabled', false).removeClass('loading');
        this.$body.removeClass('disabled');

        // call init callback
        this.onOpen();

        // open, append to dom
        this.$bg.show();
        setTimeout(() => {
            // timeout needed for css anim to work
            this.$bg.addClass('open');
        }, 5);

        // blur behind
        if (this.options.blurBehind) {
            $('#app__header, .to-blur').addClass('blurred');
        }

        // autofocus on first input
        if (this.options.autoFocus) {
            setTimeout(() => {
                this.$('input:first, textarea:first').focus();
            }, 50);
        }

        Dialog.currentlyOpen = this;
        this.bindEvents();
        this.trigger('open', { dialog: this });
        Events.trigger('dialog_open', { dialog: this });

        return this;
    }

    close(delay = 300): Dialog {
        this.$bg.removeClass('open');
        setTimeout(() => {
            // destroy after css anim finished
            if (this.options.destroyOnClose) {
                this.destroy();
            } else {
                this.$bg.hide();
            }
            $('html').css('overflow', this.originalBodyOverflow);
        }, delay);

        // blur behind
        if (this.options.blurBehind) {
            $('#app__header, .to-blur').removeClass('blurred');
        }

        Dialog.currentlyOpen = null;

        this.unbindEvents();

        if (typeof this.options.onClose === 'function') {
            setTimeout(() => {
                this.options.onClose.call(this, this, this.openData);
            }, delay);
        }

        this.trigger('close', { dialog: this });

        return this;
    }

    submit(): Dialog {
        this.hideError();
        if (typeof this.options.onSubmit === 'function') {
            const promise: Promise<any> = this.options.onSubmit.call(this, this, this.openData);
            if (promise && promise.then) {
                this.$btnSubmit.prop('disabled', true).addClass('loading');
                this.$body.addClass('disabled');
                promise
                    .then(() => {
                        setTimeout(() => {
                            // ajax is usually to fast, and a person can't see spinner, so wait a bit
                            this.$btnSubmit.prop('disabled', false).removeClass('loading');
                            this.$body.removeClass('disabled');
                            this.trigger('submit', { dialog: this });
                            if (this.options.closeOnSubmit) {
                                this.close();
                            }
                        }, 200);
                    })
                    .catch(resp => {
                        setTimeout(() => {
                            this.showError(typeof resp == 'string' ? resp : kuura.getMessage('js_files.msg.notifications.request_error'));
                            this.$body.removeClass('overlay-loading');
                            this.$btnSubmit.removeClass('loading');
                        }, 200);
                    });
            } else if (this.options.closeOnSubmit) {
                this.close();
            }
        } else if (this.options.closeOnSubmit) {
            this.close();
        }
        return this;
    }

    $(selector: string) {
        return this.$body.find(selector);
    }

    setTitle(title = '', icon = this.options.titleIcon): Dialog {
        if (icon.length > 3) {
            title = `<i class="${icon}"></i> ${title}`;
        }
        this.$headerTitle.html(title);
        return this;
    }

    toggleMaximize = (state?: boolean): Dialog => {
        const isMaximized = typeof state === 'undefined' ? this.$dialog.hasClass('maximized') : !state;
        this.$dialog.addRemoveClass(!isMaximized, 'maximized');
        this.$btnMaximize.find('i')[0].className = isMaximized ? 'iki iki-expand' : 'iki iki-reduce';

        // call callback after anim
        if (typeof this.options.onExpand === 'function') {
            setTimeout(() => {
                this.options.onExpand.call(this, !isMaximized);
            }, 400);
        }
        return this;
    };

    isOpen(): boolean {
        return Dialog.currentlyOpen === this;
    }

    showError(text: string = kuura.getMessage('js_files.msg.notifications.error_loading_dialog')) {
        this.$header.after(this.$error);
        this.$error.text(text);
    }

    hideError() {
        this.$error.detach();
    }

    // EVENTS
    on(event: DialogEvent, callback: (eventObject: JQueryEventObject, ...args: any[]) => any) {
        $(this.$dialog).on(event as any, callback);
    }

    off(event: DialogEvent, callback: (eventObject: JQueryEventObject, ...args: any[]) => any) {
        $(this.$dialog).off(event as any, callback);
    }

    trigger(event: DialogEvent, data?: any) {
        $(this.$dialog).trigger(event as string, data);
    }

    private bindEvents() {
        this.$btnClose.click(() => this.close());
        this.$btnMaximize.click(() => this.toggleMaximize());
        this.$btnCancel.off().click(() => {
            this.close();
            if (this.options.onCancel) {
                this.options.onCancel.call(this, this, this.openData);
            }
        });
        this.$btnSubmit.off().click(() => this.submit());

        if (this.options.closeOnEsc) {
            $('body').on('keydown.ikdialog', e => {
                if (e.keyCode === 27) {
                    this.close();
                }
            });
        }
    }

    private unbindEvents() {
        this.$btnClose.off('click');
        this.$btnMaximize.off('click');
        this.$btnCancel.off('click');
        this.$btnSubmit.off('click');
        if (this.options.closeOnEsc) {
            $('body').off('keydown.ikdialog');
        }
    }

    private onOpen() {
        // clear error msg that might have been shown before
        this.hideError();

        // if rendering using template, clear body so we don't see the old state when reopening dialog
        if (typeof this.options.bodyTemplate === 'function') {
            this.$body.empty();
        }

        // if onOpen callback exists call it, and do some stuff if it returns a promise
        if (typeof this.options.onOpen === 'function') {
            const promise: Promise<any> = this.options.onOpen.call(this, this, this.openData);
            if (promise && promise.then) {
                // put dialog into loading state
                this.$btnSubmit.prop('disabled', true);
                this.$body.addClass('disabled overlay-loading');

                promise
                    .then(data => {
                        // clear loading state
                        this.$btnSubmit.prop('disabled', false);
                        this.$body.removeClass('disabled overlay-loading');

                        // render body from optional template
                        if (typeof this.options.bodyTemplate === 'function') {
                            this.$body.html(this.options.bodyTemplate(data));
                        }

                        // trigger event
                        this.trigger('init', { dialog: this });
                    })
                    .catch(() => {
                        // promise/loading dialog failed
                        this.showError();
                        this.$body.removeClass('overlay-loading');
                    });
            }
        }
    }

    private restoreInitailState() {
        this.$body.find('input[type="text"]').val('');
        this.$body.find('select').each((_index: number, item: HTMLSelectElement) => {
            item.selectedIndex = 0;
        });
    }
}
